// src/VisaPurposeSelector.tsx
import React, { useState, useEffect } from 'react';
import { MAIN_VISA_CATEGORIES, MainCategory, SubCategory } from './visaCategories';

interface VisaPurposeSelectorProps {
  value: string;
  onChange: (officialString: string) => void;
  error?: boolean;
}

export const VisaPurposeSelector: React.FC<VisaPurposeSelectorProps> = ({ value, onChange, error }) => {
  const [selectedMainId, setSelectedMainId] = useState<string>('');
  const [selectedSubString, setSelectedSubString] = useState<string>('');

  // Synchronize state from existing form value if present
  useEffect(() => {
    if (!value) return;

    for (const main of MAIN_VISA_CATEGORIES) {
      for (const sub of main.subCategories) {
        if (sub.officialString === value || value.startsWith(main.officialPrefix)) {
          setSelectedMainId(main.id);
          setSelectedSubString(value);
          return;
        }
      }
    }
  }, [value]);

  const handleMainCategorySelect = (main: MainCategory) => {
    setSelectedMainId(main.id);
    const defaultSub = main.subCategories[0];
    if (defaultSub) {
      setSelectedSubString(defaultSub.officialString);
      onChange(defaultSub.officialString);
    }
  };

  const handleSubCategorySelect = (sub: SubCategory) => {
    setSelectedSubString(sub.officialString);
    onChange(sub.officialString);
  };

  const currentMain = MAIN_VISA_CATEGORIES.find((m) => m.id === selectedMainId);

  return (
    <div
      style={{
        background: '#ffffff',
        border: error ? '2px solid #ef4444' : '1px solid #cbd5e1',
        borderRadius: '12px',
        padding: '1.25rem',
        marginTop: '0.35rem',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
      }}
    >
      <div style={{ fontSize: '0.875rem', fontWeight: '700', color: '#1e293b', marginBottom: '0.75rem' }}>
        Step 1: Choose Main Visa Category
      </div>

      {/* Main Categories Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '0.65rem',
          marginBottom: '1.25rem',
        }}
      >
        {MAIN_VISA_CATEGORIES.map((main) => {
          const isSelected = main.id === selectedMainId;
          return (
            <button
              key={main.id}
              type="button"
              onClick={() => handleMainCategorySelect(main)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1rem',
                minHeight: '52px',
                borderRadius: '10px',
                border: isSelected ? '2px solid #2563eb' : '1px solid #e2e8f0',
                background: isSelected ? '#eff6ff' : '#ffffff',
                color: isSelected ? '#1e3a8a' : '#334155',
                fontWeight: isSelected ? '700' : '500',
                fontSize: '0.875rem',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.15s ease',
              }}
            >
              <span style={{ fontSize: '1.25rem' }}>{main.icon}</span>
              <span>{main.label}</span>
            </button>
          );
        })}
      </div>

      {/* Sub Category Selection */}
      {currentMain && (
        <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>
            Step 2: Select Specific Sub-Purpose for {currentMain.label}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {currentMain.subCategories.map((sub) => {
              const isSubSelected = sub.officialString === selectedSubString;
              return (
                <label
                  key={sub.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.65rem',
                    padding: '0.65rem 1rem',
                    minHeight: '44px',
                    borderRadius: '8px',
                    border: isSubSelected ? '2px solid #10b981' : '1px solid #cbd5e1',
                    background: isSubSelected ? '#ecfdf5' : '#ffffff',
                    color: isSubSelected ? '#047857' : '#334155',
                    fontWeight: isSubSelected ? '700' : '400',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <input
                    type="radio"
                    name="visa_sub_purpose"
                    checked={isSubSelected}
                    onChange={() => handleSubCategorySelect(sub)}
                    style={{ accentColor: '#10b981', width: '18px', height: '18px' }}
                  />
                  <span>{sub.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
